import { getProductConfig } from '@/app/config/products.config';
import { usePmeTarifacao } from '@/features/proposal-analyses/pme/use-pme-tarifacao.hook';
import { PmeLivesByStipulantSection } from '@/features/proposals/components/pme/steps/sections/lives-by-stipulant-section.component';
import { useGroupLifeLivesCount } from '@/features/proposals/hooks/group-life/use-lives-count.hook';
import { useGroupLifeProposalLives } from '@/features/proposals/hooks/group-life/use-proposal-lives.hook';
import {
  QuoteParamsProvider,
  useQuoteParams,
} from '@/features/quotes/context/quote-params.context';
import { RecalculationProgressOverlay } from '@/features/quotes/components/quote-steps/group-life/recalculation-progress-overlay.component';
import { useCalculateGroupLifeAnalysis } from '@/features/quotes/hooks/group-life/use-calculate-group-life-analysis.hook';
import { useMembersRatingProgress } from '@/features/quotes/hooks/group-life/use-members-rating-progress.hook';
import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import { useGroupLifeQuoteDetails } from '@/features/quotes/hooks/group-life/use-group-life-quote-details.hook';
import { resolveMonthlyCoverageBreakdown } from '@/features/quotes/utils/group-life/monthly-coverage-premium.util';
import { dailyAllowanceDetails } from '@/features/quotes/utils/group-life/daily-allowance.util';
import { useGroupLifeQuoteVersions } from '@/features/quotes/hooks/group-life/use-group-life-quote-versions.hook';
import { useGroupLifeCreditAnalysisPdf } from '@/features/quotes/hooks/group-life/use-group-life-credit-analysis-pdf.hook';
import { useGroupLifeUnderwritingMessages } from '@/features/quotes/hooks/group-life/use-group-life-underwriting-messages.hook';
import { useRejectGroupLifeAnalysis } from '@/features/quotes/hooks/group-life/use-reject-group-life-analysis.hook';
import { useSubmitGroupLifeAnalysisCalculation } from '@/features/quotes/hooks/group-life/use-submit-group-life-analysis-calculation.hook';
import { parsePercent } from '@/features/quotes/schemas/commercialization-percent.schema';
import {
  formatCurrencyBRL,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import { resolveAverageRatePerMille } from '@/features/quotes/utils/group-life/average-rate.util';
import {
  boundDisplayedCapital,
  resolveMemberDisplayCapital,
} from '@/features/quotes/utils/group-life/effective-capital';
import { benefitSumInsuredByCoverage } from '@/features/quotes/utils/group-life/benefit-sum-insured.util';
import { firstMeaningfulValue } from '@/features/quotes/utils/group-life/coverage-unit-premium.util';
import { isServiceCoverage } from '@/features/quotes/utils/group-life/service-coverage.util';
import type {
  GroupLifeFrontCommercialization,
  GroupLifeQuoteDetails,
} from '@/services/group-life';
import {
  coverageTotalSumInsured,
  coverageUnitSumInsured,
  sumCoverageSumInsured,
} from './coverage-rates';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import {
  Button,
  Field,
  FieldTextarea,
  Spinner,
  Tag,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import {
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Home,
  Info,
  Users,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AnalysisTabsCard,
  type AnalysisTabItem,
} from '../components/analysis-tabs-card.component';
import { mapUnderwritingMessagesToCriteria } from './acceptance-criteria';
import { PmeAnalysisPremiumCard } from './components/pme-analysis-premium-card.component';
import {
  PmeAnalysisCommercializationSection,
  PmeAnalysisVersionsSection,
} from './components/pme-analysis-sections.component';
import {
  PmeAcceptanceCriteriaTab,
  PmeAfastadosTab,
  PmeAgeCapitalTab,
  PmeCoveragesServicesTab,
} from './components/pme-analysis-tab-contents.component';
import {
  createLifeCapitalResolver,
  isStoredSalaryCapital,
} from './life-capital';
import type {
  PmeAgeCapitalRow,
  PmeAnalysisCommercialization,
  PmeAnalysisPremium,
  PmeCoverageLine,
  PmeRemovedLifeRow,
  PmeServiceLine,
} from './pme-analysis.mock';
import { QuoteActivitiesSection } from '@/features/analyses/activities';

type Props = {
  policyId: string;
};

const ICON_CLASS = 'h-4 w-4';
/** Vidas por página na tabela "Lista de Vidas". O usuário pode trocar depois. */
const LIVES_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];
const DEFAULT_LIVES_PAGE_SIZE = LIVES_PAGE_SIZE_OPTIONS[0];

function sexLabel(sex?: string): string {
  if (sex === 'M') return 'Masculino';
  if (sex === 'F') return 'Feminino';
  return '—';
}

function ageFromBirthDate(birthDate?: string): string {
  const match = (birthDate ?? '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return '—';
  const [, day, month, year] = match;
  const birth = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(birth.getTime())) return '—';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 && age < 130 ? String(age) : '—';
}

function toDisplayPercent(raw?: string): string {
  const value = (raw ?? '').trim();
  if (!value) return '0%';
  return value.endsWith('%') ? value : `${value}%`;
}

/** Monta o estado dos 4 campos de comercialização a partir do shape do BFF.
 *  Agravo/desconto vêm de `surchargePercent`/`actuarialDiscountPercent`
 *  (PolicyDiscountLoadingList tipos S/D), não mais de `agencyPercent`. */
function toCommercializationState(
  c?: GroupLifeFrontCommercialization
): PmeAnalysisCommercialization {
  return {
    brokeragePercent: toDisplayPercent(c?.brokeragePercent),
    agencyPercent: toDisplayPercent(c?.agencingPercent || c?.agencyPercent),
    surcharge: toDisplayPercent(c?.surchargePercent),
    actuarialDiscount: toDisplayPercent(c?.actuarialDiscountPercent),
  };
}

function contractTypeLabel(insuranceType?: string): string {
  switch (insuranceType?.trim().toUpperCase()) {
    case '4':
    case 'TRANSFER':
      return 'Transferência';
    case '3':
    case 'RENEWAL':
      return 'Renovação';
    case '1':
    case 'NEW':
    default:
      return 'Nova';
  }
}

function PmeAnalysisBreadcrumb() {
  return (
    <Page.Breadcrumb
      items={[
        {
          label: 'Pagina Inicial',
          href: '/dashboard',
          iconLeft: <Home className='h-4 w-4' />,
        },
        { label: 'Subscrição' },
        { label: 'Cotações em Análise', href: '/analyses' },
        { label: 'Análise PME', active: true },
      ]}
    />
  );
}

export function PmeAnalysisPage({ policyId }: Props) {
  return (
    <QuoteParamsProvider>
      <PmeAnalysisPageInner policyId={policyId} />
    </QuoteParamsProvider>
  );
}

function PmeAnalysisPageInner({ policyId }: Props) {
  const navigate = useNavigate();
  const { policyId: contextPolicyId, setParams } = useQuoteParams();

  useEffect(() => {
    setParams({ policyId });
  }, [policyId, setParams]);

  const { data: quoteDetails, isLoading } = useGroupLifeQuoteDetails();

  if (!contextPolicyId || isLoading) {
    return (
      <Page.Root>
        <PmeAnalysisBreadcrumb />
        <Page.Content className='py-4'>
          <div className='flex justify-center py-24'>
            <Spinner size='lg' colorScheme='blue' />
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  if (!quoteDetails) {
    return (
      <Page.Root>
        <PmeAnalysisBreadcrumb />
        <Page.Content className='flex flex-col items-start gap-4 py-4'>
          <Text className='text-sm text-alba-gray-600'>
            Cotação PME não encontrada.
          </Text>
          <Button
            variant='primary'
            size='md'
            onClick={() => void navigate({ to: '/quotes' })}
          >
            Ir para Minhas Cotações
          </Button>
        </Page.Content>
      </Page.Root>
    );
  }

  if (Number(quoteDetails.statusPortal) !== PortalStatus.QuoteUnderAnalysis) {
    return (
      <Page.Root>
        <PmeAnalysisBreadcrumb />
        <Page.Content className='flex flex-col items-start gap-4 py-4'>
          <Text className='text-sm text-alba-gray-600'>
            A cotação não está em análise (status{' '}
            {quoteDetails.statusPortal ?? 'indefinido'}).
          </Text>
          <Button
            variant='primary'
            size='md'
            onClick={() => void navigate({ to: '/quotes' })}
          >
            Ir para Minhas Cotações
          </Button>
        </Page.Content>
      </Page.Root>
    );
  }

  return <PmeAnalysisContent policyId={policyId} quoteDetails={quoteDetails} />;
}

function PmeAnalysisContent({
  policyId,
  quoteDetails,
}: {
  policyId: string;
  quoteDetails: GroupLifeQuoteDetails;
}) {
  const navigate = useNavigate();

  const versionsQuery = useGroupLifeQuoteVersions(policyId);
  const versions = versionsQuery.data?.versions ?? [];
  // A seção aparece sempre que houver histórico — mesmo com uma única versão
  // (Version_0), o subscritor vê o estado original da cotação.
  const showVersions = versions.length > 0;

  const underwritingQuery = useGroupLifeUnderwritingMessages(policyId);

  const coverageSchemaQuery = useGroupLifeCoverageSchema({
    productCode: quoteDetails.productCode,
  });
  const coverageNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const cov of coverageSchemaQuery.data?.coverages ?? []) {
      map.set(cov.code, cov.name);
    }
    return map;
  }, [coverageSchemaQuery.data]);
  // Diárias (DH/DIT): "30 diárias × R$ 100,00", da opção gravada no plano.
  const dailyAllowanceByCoverage = useMemo(
    () =>
      dailyAllowanceDetails(
        coverageSchemaQuery.data,
        quoteDetails.plan?.coverages
      ),
    [coverageSchemaQuery.data, quoteDetails.plan?.coverages]
  );

  const acceptanceCriteria = useMemo(
    () =>
      mapUnderwritingMessagesToCriteria(underwritingQuery.data, undefined, {
        coverageNameByCode,
      }),
    [underwritingQuery.data, coverageNameByCode]
  );
  const serviceCoverageKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const coverage of coverageSchemaQuery.data?.coverages ?? []) {
      if (isServiceCoverage(coverage.code, coverage.name, coverage.group)) {
        keys.add(coverage.code);
        keys.add(coverage.name);
      }
    }
    return keys;
  }, [coverageSchemaQuery.data]);
  /**
   * Itens CONTRATADOS no plano, já separados nas duas listas da aba.
   *
   * Boa parte das assistências — e os auxílios — não tem prêmio próprio e por
   * isso não gera linha no demonstrativo do cálculo. As duas tabelas se montam
   * a partir do demonstrativo, então sem esta seleção esses itens sumiriam da
   * tela: é daqui que sai a segunda passada que completa cada lista.
   */
  const selectedCoveragesByKind = useMemo(() => {
    const selections = quoteDetails.plan?.coverages ?? {};
    const services: { code: string; name: string }[] = [];
    const coverages: { code: string; name: string }[] = [];

    for (const coverage of coverageSchemaQuery.data?.coverages ?? []) {
      if (!selections[coverage.code]?.enabled) continue;
      const entry = { code: coverage.code, name: coverage.name };
      if (isServiceCoverage(coverage.code, coverage.name, coverage.group)) {
        services.push(entry);
      } else {
        coverages.push(entry);
      }
    }

    return { services, coverages };
  }, [coverageSchemaQuery.data, quoteDetails.plan?.coverages]);

  const pricingQuery = usePmeTarifacao(policyId, 'quote', undefined, 1000);
  const pricingLives = useMemo(
    () => pricingQuery.data?.pages.flatMap(page => page.data) ?? [],
    [pricingQuery.data]
  );
  // A tarifação chega em páginas de 1000. Somar só a primeira (1000 × capital
  // da vida) e exibir como capital do grupo foi o que mostrou R$ 2.700.000,00
  // até o F5 reler o snapshot. Só vale usar essa soma quando não há próxima
  // página — e ainda assim só na falta do capital do grupo.
  const pricingCapitalComplete =
    pricingQuery.isSuccess &&
    !pricingQuery.isPlaceholderData &&
    !pricingQuery.hasNextPage &&
    !pricingQuery.isFetchingNextPage;
  // Sem isso a reserva nunca fica completa: a página pede 1000 e não pede o
  // resto, então uma cotação com mais vidas ficaria sem capital se o grupo
  // ainda não tivesse o total.
  useEffect(() => {
    if (
      !pricingQuery.isSuccess ||
      pricingQuery.isPlaceholderData ||
      !pricingQuery.hasNextPage ||
      pricingQuery.isFetchingNextPage
    ) {
      return;
    }
    void pricingQuery.fetchNextPage();
  }, [
    pricingQuery.isSuccess,
    pricingQuery.isPlaceholderData,
    pricingQuery.hasNextPage,
    pricingQuery.isFetchingNextPage,
    pricingQuery.fetchNextPage,
  ]);
  const sumInsuredByCoverage = useMemo(
    () => coverageTotalSumInsured(pricingLives),
    [pricingLives]
  );
  const unitSumInsuredByCoverage = useMemo(
    () => coverageUnitSumInsured(pricingLives),
    [pricingLives]
  );

  // Lista inteira de vidas: ainda alimenta as abas Idade / Capital e
  // Afastados, que agregam todas as vidas.
  const { lives, isLoading: isLoadingLives } = useGroupLifeProposalLives(
    policyId,
    quoteDetails
  );

  // Contagem de vidas da análise, feita pelo backend (`/members/page`) por
  // target — declarada aqui porque entra na resolução do capital do auxílio,
  // logo abaixo. As tabelas, separadas por estipulante, buscam as próprias
  // páginas.
  const livesTargetIds = useMemo(
    () => [
      '__STIPULATOR__',
      ...(quoteDetails.subStipulators ?? []).map(sub => sub.id),
    ],
    [quoteDetails.subStipulators]
  );
  const livesCount = useGroupLifeLivesCount({
    policyId,
    targetIds: livesTargetIds,
  });
  const totalLives = livesCount.total;

  /**
   * IS fixa das coberturas de seleção de benefício (os auxílios), do catálogo.
   * É a fonte primária do capital delas: não depende de a cotação já ter sido
   * calculada nem de a tarifação ter chegado.
   */
  const benefitSumInsured = useMemo(
    () =>
      benefitSumInsuredByCoverage(
        coverageSchemaQuery.data,
        quoteDetails.plan?.coverages
      ),
    [coverageSchemaQuery.data, quoteDetails.plan?.coverages]
  );

  /**
   * Capital de UMA vida — o `SumInsured` do tarifado, o campo que a coluna
   * "Valor por Segurado" usava quando o auxílio era listado em Serviços.
   */
  const resolveUnitSumInsured = useCallback(
    (code: string, name: string): number | undefined =>
      firstMeaningfulValue(
        unitSumInsuredByCoverage.get(code),
        unitSumInsuredByCoverage.get(name)
      ),
    [unitSumInsuredByCoverage]
  );

  /**
   * Capital Segurado TOTAL de uma cobertura — o do grupo, que é o que a coluna
   * pede e o que a taxa (prêmio ÷ capital × 1000) precisa.
   *
   * A fonte é o capital tarifado de cada estipulante
   * (`plan.coverageSumInsured` do master + o de cada subestipulante), somado
   * aqui. A lista de estipulantes mostra cada um separado; esta aba mostra o
   * total da apólice. A soma das vidas tarifadas fica só de reserva (cotação
   * ainda não calculada).
   *
   * No auxílio o campo não vem: o catálogo dá o capital de UMA vida
   * (`DefaultSI`), então o total é ele × vidas. Sem a contagem ainda carregada
   * devolve `undefined`, e não zero: a linha mostra "—" em vez de um número
   * errado por um instante.
   */
  const coverageSumInsured = useMemo(
    () =>
      sumCoverageSumInsured([
        quoteDetails.plan?.coverageSumInsured,
        ...(quoteDetails.subStipulators ?? []).map(
          sub => sub.coverageSumInsured
        ),
      ]),
    [quoteDetails.plan?.coverageSumInsured, quoteDetails.subStipulators]
  );
  const resolveCoverageCapital = useCallback(
    (code: string, name: string): number | undefined => {
      const perLife = firstMeaningfulValue(
        benefitSumInsured.get(code),
        benefitSumInsured.get(name)
      );
      if (perLife != null) {
        return totalLives > 0 ? perLife * totalLives : undefined;
      }

      const fromGroup = coverageSumInsured?.[code];
      if (fromGroup != null && fromGroup > 0) return fromGroup;
      if (!pricingCapitalComplete) return undefined;

      return firstMeaningfulValue(
        sumInsuredByCoverage.get(code),
        sumInsuredByCoverage.get(name),
        resolveUnitSumInsured(code, name)
      );
    },
    [
      benefitSumInsured,
      coverageSumInsured,
      pricingCapitalComplete,
      sumInsuredByCoverage,
      resolveUnitSumInsured,
      totalLives,
    ]
  );
  // Clicar numa versão encaminha para a tela de resumo do wizard PME,
  // hidratando o snapshot daquela versão (via `quotationVersionId`). Sem id,
  // abre o resumo da versão atual ("Critério Atual").
  const openVersionSummary = (quotationVersionId?: number) => {
    const cfg = getProductConfig(quoteDetails.productCode);
    void navigate({
      to: '/quotes/products/new/$id',
      params: { id: policyId },
      search: {
        productId: '',
        category: cfg?.schemaCategory ?? 'GROUP_LIFE_INSURANCE',
        productCode: quoteDetails.productCode,
        stepCode: 'CALCULATION',
        quoteFlow: 'view',
        quotationVersionId:
          quotationVersionId != null ? String(quotationVersionId) : undefined,
      },
    });
  };

  const [commercialization, setCommercialization] =
    useState<PmeAnalysisCommercialization>(() =>
      toCommercializationState(quoteDetails.commercialization)
    );
  const [observations, setObservations] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // Re-hidrata os 4 campos quando o GET :policyId volta fresco (ex.: após o
  // recálculo, cujo mutation invalida `quote-details`). Durante a edição o
  // objeto não muda de referência, então não sobrescreve o que o usuário
  // digitou.
  useEffect(() => {
    setCommercialization(
      toCommercializationState(quoteDetails.commercialization)
    );
  }, [quoteDetails.commercialization]);

  const calculateAnalysis = useCalculateGroupLifeAnalysis();
  const ratingProgress = useMembersRatingProgress({
    policyId,
    enabled: calculateAnalysis.isPending,
  });
  const submitAnalysis = useSubmitGroupLifeAnalysisCalculation();
  const rejectAnalysis = useRejectGroupLifeAnalysis();

  const isBusy =
    calculateAnalysis.isPending ||
    submitAnalysis.isPending ||
    rejectAnalysis.isPending;

  const handleSaveCommercialization = async (): Promise<boolean> => {
    if (isBusy) return false;
    const brokerage = parsePercent(commercialization.brokeragePercent);
    const agencying = parsePercent(commercialization.agencyPercent);
    try {
      await calculateAnalysis.mutateAsync({
        policyId,
        payload: {
          brokeragePercent: brokerage,
          agencyingPercent: agencying,
          surchargePercent: parsePercent(commercialization.surcharge),
          actuarialDiscountPercent: parsePercent(
            commercialization.actuarialDiscount
          ),
        },
      });
      toaster.create({
        type: 'success',
        title: 'Cotação recalculada',
        description:
          'As alterações de comercialização foram aplicadas na análise.',
      });
      return true;
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao recalcular',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível recalcular a cotação. Tente novamente.',
      });
      return false;
    }
  };

  const handleApprove = () => {
    if (isBusy) return;
    submitAnalysis.mutate(
      { policyId },
      {
        onSuccess: () => {
          toaster.create({
            type: 'success',
            title: 'Cotação aprovada',
            description: 'A cotação foi liberada como "Cotação Calculada".',
          });
          void navigate({ to: '/analyses' });
        },
        onError: error => {
          toaster.create({
            type: 'error',
            title: 'Erro ao aprovar',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível aprovar a cotação. Tente novamente.',
          });
        },
      }
    );
  };

  const cancelReject = () => {
    setIsRejecting(false);
    setObservations('');
  };

  const handleReject = () => {
    if (isBusy) return;
    const trimmed = observations.trim();
    if (!trimmed) return;
    rejectAnalysis.mutate(
      { policyId, description: trimmed },
      {
        onSuccess: () => {
          toaster.create({
            type: 'success',
            title: 'Cotação recusada',
            description: 'A cotação foi encerrada com o motivo registrado.',
          });
          void navigate({ to: '/analyses' });
        },
        onError: error => {
          toaster.create({
            type: 'error',
            title: 'Erro ao recusar',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível recusar a cotação. Tente novamente.',
          });
        },
      }
    );
  };

  const stipulator = quoteDetails.stipulator;
  // Prêmio do snapshot VIVO da cotação: o recálculo da análise grava o prêmio
  // na própria apólice (calculateEx + updateEx). Ler de uma versão exigiria
  // criar uma a cada save — custo que não se paga, já que só a Version_0 tem
  // uso funcional (restaurada no Recusar).
  const calc = quoteDetails.calculation;
  const quoteNumber = quoteDetails.proposalNumber || policyId;

  const monthlyTotal =
    calc?.monthlyPremium ?? (calc?.totalPremium ? calc.totalPremium / 12 : 0);
  const monthlyIof = calc?.iof ? calc.iof / 12 : 0;
  const monthlyNet = Math.max(monthlyTotal - monthlyIof, 0);

  const premium: PmeAnalysisPremium = {
    totalValue: formatCurrencyBRL(monthlyTotal),
    netValue: formatCurrencyBRL(monthlyNet),
    iofValue: formatCurrencyBRL(monthlyIof),
    index: '—',
    paymentFrequency: 'Mensal',
    product: 'Seguro PME - Vida em Grupo',
    contractType: contractTypeLabel(stipulator?.insuranceType),
    status: 'Em Análise',
  };

  const coverageRows: PmeCoverageLine[] = [];
  const serviceRows: PmeServiceLine[] = [];
  /**
   * "Taxa Média Mensal" do rodapé: soma dos prêmios mensais das coberturas
   * dividida pelo capital segurado total da cobertura Morte, vezes mil (‰).
   * Assistências ficam de fora. O capital da Morte é o mesmo da coluna dessa
   * linha.
   */
  let coveredMonthlyPremium = 0;
  let morteCapital: number | undefined;
  const isMorteCoverage = (code: string, name: string) =>
    code === '9300001' || name.trim().toLocaleLowerCase('pt-BR') === 'morte';
  // Demonstrativo MENSAL por cobertura (fecha no prêmio mensal) — mesma
  // conta do Demonstrativo da proposta.
  const monthlyBreakdown = resolveMonthlyCoverageBreakdown(calc);
  for (const item of monthlyBreakdown) {
    const name = coverageNameByCode.get(item.description) ?? item.description;
    if (
      serviceCoverageKeys.has(item.description) ||
      serviceCoverageKeys.has(name) ||
      isServiceCoverage(item.description, name)
    ) {
      // "Valor Mensal": o total mensal do demonstrativo (com IOF).
      const monthlyValue = item.total;
      // "Valor por Segurado": o valor mensal ÷ vidas da cotação. Sem a contagem
      // ainda carregada mostra "—" em vez de um número errado por um instante.
      serviceRows.push({
        name,
        valuePerInsured:
          monthlyValue != null && totalLives > 0
            ? formatCurrencyBRL(monthlyValue / totalLives)
            : '—',
        monthlyValue: formatCurrencyBRL(monthlyValue),
      });
      continue;
    }

    // "Capital Segurado (I$) Total" — o capital do GRUPO, inclusive no auxílio
    // (valor do catálogo × vidas). A taxa da linha usa o capital dela. O
    // rodapé usa só o capital da cobertura Morte.
    const totalSumInsured = resolveCoverageCapital(item.description, name);
    const monthlyPremium = item.total;
    coveredMonthlyPremium += monthlyPremium;
    if (isMorteCoverage(item.description, name) && totalSumInsured != null) {
      morteCapital = totalSumInsured;
    }
    coverageRows.push({
      name,
      detail:
        dailyAllowanceByCoverage.get(item.description) ??
        dailyAllowanceByCoverage.get(name),
      totalInsuredCapital:
        totalSumInsured == null ? '—' : formatCurrencyBRL(totalSumInsured),
      // Taxa derivada da própria linha: prêmio ÷ capital × 1000.
      averageRate: resolveAverageRatePerMille(monthlyPremium, totalSumInsured),
      monthlyPremium: formatCurrencyBRL(monthlyPremium),
    });
  }

  // Coberturas contratadas sem linha no demonstrativo entram mesmo assim, com
  // "—" no que o cálculo não informa. É o caso dos auxílios: contratados no
  // plano, sem prêmio próprio no demonstrativo.
  const coverageRowNames = new Set(coverageRows.map(row => row.name));
  for (const coverage of selectedCoveragesByKind.coverages) {
    if (coverageRowNames.has(coverage.name)) continue;
    // Mesma resolução do laço acima.
    const totalSumInsured = resolveCoverageCapital(
      coverage.code,
      coverage.name
    );
    coverageRows.push({
      name: coverage.name,
      detail: dailyAllowanceByCoverage.get(coverage.code),
      totalInsuredCapital:
        totalSumInsured == null ? '—' : formatCurrencyBRL(totalSumInsured),
      // Sem linha no demonstrativo não há prêmio — e sem prêmio não há taxa.
      averageRate: '—',
      monthlyPremium: '—',
    });
  }

  // Assistências contratadas sem linha no demonstrativo entram mesmo assim,
  // com "—" no que o cálculo não informa: sem total não há valor mensal nem
  // por segurado.
  const serviceRowNames = new Set(serviceRows.map(row => row.name));
  for (const coverage of selectedCoveragesByKind.services) {
    if (serviceRowNames.has(coverage.name)) continue;
    serviceRows.push({
      name: coverage.name,
      valuePerInsured: '—',
      monthlyValue: '—',
    });
  }

  if (morteCapital == null) {
    const morte = selectedCoveragesByKind.coverages.find(coverage =>
      isMorteCoverage(coverage.code, coverage.name)
    );
    if (morte) {
      morteCapital = resolveCoverageCapital(morte.code, morte.name);
    }
  }

  // Soma dos prêmios mensais ÷ capital total da Morte, × 1.000 (‰).
  const averageMonthlyRate = resolveAverageRatePerMille(
    morteCapital == null ? null : coveredMonthlyPremium,
    morteCapital
  );

  // O capital exibido vem da tarifação (o que o motor tarifa): segura as
  // tabelas até ela chegar, senão aparece antes o valor do store (salário em
  // Múltiplo Salarial, capital da importação nas demais).
  const isLoadingLifeCapital = isLoadingLives || pricingQuery.isLoading;

  const resolveLifeCapital = useMemo(
    () => createLifeCapitalResolver(pricingLives),
    [pricingLives]
  );
  // Múltiplo Salarial grava o salário. A tarifação, quando casa, já traz o
  // capital efetivo; se o número continua o salário, multiplica aqui — a
  // mesma conta da coluna Capital da lista de vidas.
  const capitalShown = useCallback(
    (life: (typeof lives)[number]) => {
      const resolved = resolveLifeCapital(life);
      if (quoteDetails.plan?.modality !== 'MULTIPLE_SALARY') return resolved;
      if (!isStoredSalaryCapital(resolved, life.capital)) {
        return boundDisplayedCapital(resolved, quoteDetails.plan);
      }
      return (
        resolveMemberDisplayCapital(life.capital, quoteDetails.plan, {
          multiplySalary: true,
        }) || resolved
      );
    },
    [quoteDetails.plan, resolveLifeCapital]
  );

  const ageCapitalRows: PmeAgeCapitalRow[] = useMemo(() => {
    const groups = new Map<
      string,
      { age: string; totalCapital: number; count: number }
    >();
    for (const life of lives) {
      const age = ageFromBirthDate(life.birthDate);
      const capital = parseCurrencyInput(capitalShown(life)) ?? 0;
      const existing = groups.get(age);
      if (existing) {
        existing.count += 1;
        existing.totalCapital += capital;
      } else {
        groups.set(age, { age, totalCapital: capital, count: 1 });
      }
    }
    return Array.from(groups.values())
      .sort((a, b) => {
        const ageA = Number(a.age);
        const ageB = Number(b.age);
        const aValid = Number.isFinite(ageA);
        const bValid = Number.isFinite(ageB);
        if (aValid && bValid && ageA !== ageB) return ageA - ageB;
        if (aValid !== bValid) return aValid ? -1 : 1;
        return 0;
      })
      .map(group => ({
        age: group.age,
        capital: formatCurrencyBRL(group.totalCapital),
        lives: String(group.count),
      }));
  }, [capitalShown, lives]);

  const afastados: PmeRemovedLifeRow[] = lives
    .filter(life => life.status === 'inactive')
    .map(life => ({
      name: life.name,
      sex: sexLabel(life.sex),
      age: ageFromBirthDate(life.birthDate),
      capital: capitalShown(life),
      cids: (life.cids ?? []).filter(Boolean),
    }));

  /**
   * Relatório de crédito (WI 2224). Só aparece aqui, na tela da mesa: o
   * backend barra perfil de corretor e o relatório não é servido pela rota
   * genérica de documentos da cotação.
   */
  const creditAnalysisPdf = useGroupLifeCreditAnalysisPdf();

  const handleDownloadCreditAnalysis = async () => {
    try {
      await creditAnalysisPdf.mutateAsync(policyId);
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao gerar o relatório de crédito',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível gerar o relatório de crédito do estipulante.',
      });
    }
  };

  const tabs: AnalysisTabItem[] = [
    {
      id: 'criteria',
      label: 'Critérios de Aceitação',
      icon: <Info className={ICON_CLASS} />,
      content: <PmeAcceptanceCriteriaTab criteria={acceptanceCriteria} />,
    },
    {
      id: 'coverages',
      label: 'Coberturas e Serviços',
      icon: <Clock className={ICON_CLASS} />,
      content: (
        <PmeCoveragesServicesTab
          coverages={coverageRows}
          services={serviceRows}
          averageMonthlyRate={averageMonthlyRate}
        />
      ),
    },
    {
      id: 'age-capital',
      label: 'Idade / Capital',
      icon: <Users className={ICON_CLASS} />,
      content: (
        <PmeAgeCapitalTab
          rows={ageCapitalRows}
          isLoading={isLoadingLifeCapital}
        />
      ),
    },
    {
      id: 'afastados',
      label: 'Afastados',
      count: afastados.length,
      icon: <Eye className={ICON_CLASS} />,
      content: (
        <PmeAfastadosTab rows={afastados} isLoading={isLoadingLifeCapital} />
      ),
    },
  ];

  return (
    <Page.Root>
      <PmeAnalysisBreadcrumb />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              Análise Cotação PME - Nº {quoteNumber}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados para validação da cotação
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            <Button
              variant='secondary'
              size='md'
              loading={creditAnalysisPdf.isPending}
              onClick={handleDownloadCreditAnalysis}
            >
              <FileText className='h-5 w-5' strokeWidth={1.6} />
              Análise de Crédito
            </Button>
            <Tag.Root variant='outline' colorScheme='yellow' size='lg'>
              <Tag.Label>Em Análise</Tag.Label>
            </Tag.Root>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='flex flex-col gap-6 py-4'>
        <PmeAnalysisPremiumCard premium={premium} quoteNumber={quoteNumber} />

        <AnalysisTabsCard tabs={tabs} />

        {showVersions && (
          <PmeAnalysisVersionsSection
            versions={versions}
            onOpenVersion={row => openVersionSummary(row.quotationVersionId)}
            onOpenCurrent={() => openVersionSummary()}
            isLoading={versionsQuery.isLoading}
          />
        )}

        <PmeAnalysisCommercializationSection
          value={commercialization}
          onChange={setCommercialization}
          onSave={handleSaveCommercialization}
        />

        <div className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
          <Text className='text-base text-alba-black-500'>
            Dados da Cotação
          </Text>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            <InfoField
              label='Razão Social'
              value={
                stipulator?.tradeName || stipulator?.responsibleName || '—'
              }
            />
            <InfoField
              label='CNPJ'
              value={
                stipulator?.registerNumber
                  ? formatRegisterNumber(stipulator.registerNumber)
                  : '—'
              }
            />
            <InfoField
              label='Responsável'
              value={stipulator?.responsibleName || '—'}
            />
            <InfoField label='E-mail' value={stipulator?.email || '—'} />
            <InfoField label='Telefone' value={stipulator?.phone || '—'} />
          </div>

          {/* O valor guardado é o salário. A coluna multiplica pela quantidade
              de salários do plano em qualquer tamanho de cotação. */}
          <PmeLivesByStipulantSection
            details={quoteDetails}
            showIncompleteStatus={false}
            policyId={policyId}
            plan={quoteDetails.plan}
            multiplySalary={quoteDetails.plan?.modality === 'MULTIPLE_SALARY'}
            showImport={false}
            showAddMember={false}
            pageSize={DEFAULT_LIVES_PAGE_SIZE}
            pageSizeOptions={LIVES_PAGE_SIZE_OPTIONS}
          />
        </div>

        <QuoteActivitiesSection policyId={policyId} />

        {isRejecting && (
          <div className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
            <Text className='text-base text-alba-black-500'>
              Motivo da recusa
            </Text>
            <Field.Root>
              <Field.Label>Descreva o motivo da recusa</Field.Label>
              <FieldTextarea
                value={observations}
                onChange={event => setObservations(event.target.value)}
                placeholder='Descreva o motivo da recusa...'
                rows={5}
              />
            </Field.Root>
          </div>
        )}

        <div className='flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between'>
          <Button
            variant='secondary'
            size='md'
            disabled={isBusy}
            onClick={() => {
              if (isRejecting) {
                cancelReject();
              } else {
                void navigate({ to: '/analyses' });
              }
            }}
          >
            {isRejecting ? 'Cancelar' : 'Sair'}
          </Button>

          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            {isRejecting ? (
              <button
                type='button'
                disabled={isBusy || !observations.trim()}
                onClick={handleReject}
                className='flex items-center justify-center gap-2 rounded-xl bg-alba-error-red-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
              >
                <XCircle className='h-4 w-4' />
                Confirmar Recusa
              </button>
            ) : (
              <>
                <Button
                  variant='primary'
                  size='md'
                  disabled={isBusy}
                  onClick={() =>
                    void navigate({
                      to: '/analyses/$policyId/pricing',
                      params: { policyId },
                    })
                  }
                >
                  Tarifação
                </Button>
                <button
                  type='button'
                  disabled={isBusy}
                  onClick={() => setIsRejecting(true)}
                  className='flex items-center justify-center gap-2 rounded-xl bg-alba-error-red-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  <XCircle className='h-4 w-4' />
                  Recusar
                </button>
                <button
                  type='button'
                  disabled={isBusy}
                  onClick={handleApprove}
                  className='flex items-center justify-center gap-2 rounded-xl bg-alba-success-green-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  <CheckCircle2 className='h-4 w-4' />
                  Aprovar
                </button>
              </>
            )}
          </div>
        </div>
      </Page.Content>
      {calculateAnalysis.isPending && (
        <RecalculationProgressOverlay
          progress={ratingProgress.progress}
          error={ratingProgress.error}
        />
      )}
    </Page.Root>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-1'>
      <Text className='font-montserrat text-sm font-bold text-alba-black-500'>
        {label}
      </Text>
      <Text className='font-montserrat text-sm text-alba-gray-600'>
        {value}
      </Text>
    </div>
  );
}
